import { getProductTypeFromRouteSlug } from './colorRoute'
export type SignalsKitClickEventName =
  | 'private_access_cta_click'
  | 'product_link_click'
  | 'site_navigation_click'
  | 'social_click'

export type SignalsKitDestinationKind =
  | 'external'
  | 'home'
  | 'magazine'
  | 'private_access'
  | 'product_category'
  | 'product_detail'
  | 'site_navigation'
  | 'social'

export type SignalsKitClickDestination = {
  destination: string
  destinationKind: SignalsKitDestinationKind
  eventName: SignalsKitClickEventName
  outbound: boolean
  productType?: string
  socialPlatform?: string
}

export const SIGNALSKIT_GLOBAL_CLICK_SKIP_SELECTOR = [
  '[data-signalskit-manual-click-scope]',
  '[data-private-access-cta]',
  '[data-private-access-inline-form]',
  '[data-instagram-follow-cta]',
  '[data-night-out-shoe-recommendation]',
  '[data-venue-intent-funnel]'
].join(',')

const PRODUCT_PATH_RE = /^\/(?:fr\/)?(ankle-boots|boots|pumps|sandals|bottines|bottes|escarpins|sandales)(?:\/|$)/
const PRODUCT_DETAIL_PATH_RE = /^\/(?:fr\/)?(ankle-boots|boots|pumps|sandals|bottines|bottes|escarpins|sandales)\/[^/?#]+\/?$/
const MAGAZINE_PATH_RE = /^\/(?:fr\/)?magazine(?:\/|$)/
const PRIVATE_ACCESS_PATH_RE = /^\/(?:join|fr\/rejoindre)\/?$/
const IGNORED_PROTOCOL_RE = /^(?:javascript|mailto|tel|sms|data|blob):/i

const isLocalDevelopmentHost = (host: string) => {
  return host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '0.0.0.0' ||
    host.endsWith('.local')
}

const isKendrickHost = (host: string) => {
  return host === 'kendrick.paris' || host.endsWith('.kendrick.paris')
}

const getSocialPlatform = (host: string) => {
  const normalizedHost = host.replace(/^www\./, '')
  if (normalizedHost === 'instagram.com' || normalizedHost.endsWith('.instagram.com')) return 'instagram'
  if (normalizedHost === 'pinterest.com' || normalizedHost.endsWith('.pinterest.com')) return 'pinterest'
  if (normalizedHost === 'facebook.com' || normalizedHost.endsWith('.facebook.com')) return 'facebook'
  if (normalizedHost === 'linkedin.com' || normalizedHost.endsWith('.linkedin.com')) return 'linkedin'
  if (normalizedHost === 'x.com' || normalizedHost === 'twitter.com') return 'twitter'
  return undefined
}

const toInternalDestination = (url: URL) => {
  return `${url.pathname || '/'}${url.search || ''}${url.hash || ''}`
}

export const classifySignalsKitClickDestination = (
  rawHref: string | null | undefined,
  currentHref = 'https://kendrick.paris/'
): SignalsKitClickDestination | null => {
  const href = rawHref?.trim()
  if (!href || href === '#' || IGNORED_PROTOCOL_RE.test(href)) return null

  let currentUrl: URL
  let url: URL

  try {
    currentUrl = new URL(currentHref, 'https://kendrick.paris/')
    url = new URL(href, currentUrl.href)
  } catch {
    return null
  }

  const sameOrigin = url.origin === currentUrl.origin
  const sameKendrickSite = isKendrickHost(url.hostname) &&
    (isKendrickHost(currentUrl.hostname) || isLocalDevelopmentHost(currentUrl.hostname))
  const internal = sameOrigin || sameKendrickSite
  const socialPlatform = getSocialPlatform(url.hostname)
  const destination = internal ? toInternalDestination(url) : url.href

  if (internal && (
    PRIVATE_ACCESS_PATH_RE.test(url.pathname) ||
    url.hash === '#private-access' ||
    url.searchParams.has('pa_source') ||
    url.searchParams.has('pa_context')
  )) {
    return {
      destination,
      destinationKind: 'private_access',
      eventName: 'private_access_cta_click',
      outbound: false
    }
  }

  if (socialPlatform) {
    return {
      destination: url.href,
      destinationKind: 'social',
      eventName: 'social_click',
      outbound: true,
      socialPlatform
    }
  }

  if (internal) {
    const productMatch = url.pathname.match(PRODUCT_PATH_RE)
    if (productMatch) {
      return {
        destination,
        destinationKind: PRODUCT_DETAIL_PATH_RE.test(url.pathname) ? 'product_detail' : 'product_category',
        eventName: 'product_link_click',
        outbound: false,
        productType: getProductTypeFromRouteSlug(productMatch[1] || '')
      }
    }

    if (MAGAZINE_PATH_RE.test(url.pathname)) {
      return {
        destination,
        destinationKind: 'magazine',
        eventName: 'site_navigation_click',
        outbound: false
      }
    }

    return {
      destination,
      destinationKind: url.pathname === '/' || url.pathname === '/fr' || url.pathname === '/fr/' ? 'home' : 'site_navigation',
      eventName: 'site_navigation_click',
      outbound: false
    }
  }

  return {
    destination,
    destinationKind: 'external',
    eventName: 'site_navigation_click',
    outbound: true
  }
}

export const normalizeSignalsKitClickLabel = (
  value: string | null | undefined,
  fallback = 'link'
) => {
  const label = value
    ?.replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim() || fallback
  return label.slice(0, 140)
}
